import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import VideoListItem from "@/components/feature/VideoListItem";
import NotFound from "@/pages/NotFound";
import {
  categories,
  getVideosByCategory,
  regionOptions,
  sortOptions,
  typeOptions,
  yearOptions,
  type Video,
} from "@/mocks/videos";
import { usePageMeta } from "@/hooks/usePageMeta";

const PAGE_SIZE = 6;

const yearThreshold = (option: string): number => {
  const now = 2026;
  if (option === "近1年") return now - 1;
  if (option === "近3年") return now - 3;
  if (option === "10年内") return now - 10;
  return 0;
};

interface ChipGroupProps {
  label: string;
  value: string;
  options: string[];
  onChange: (v: string) => void;
}

function ChipGroup({ label, value, options, onChange }: ChipGroupProps) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
      <span className="text-sm text-foreground-500 font-label shrink-0 w-16">
        {label}
      </span>
      <div className="flex items-center gap-2 flex-wrap">
        {options.map((opt) => (
          <button
            key={opt}
            onClick={() => onChange(opt)}
            className={`px-3 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
              value === opt
                ? "bg-primary-500 text-background-50"
                : "bg-background-100 text-foreground-700 hover:bg-primary-100"
            }`}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function CategoryPage() {
  const { categoryName } = useParams();
  const category = categories.find((c) => c.key === categoryName);

  const [region, setRegion] = useState("全部");
  const [year, setYear] = useState("全部");
  const [type, setType] = useState("全部");
  const [sort, setSort] = useState(sortOptions[0]);
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  const baseList = useMemo(
    () => (category ? getVideosByCategory(category.key) : []),
    [category]
  );

  const filtered = useMemo(() => {
    let list: Video[] = baseList.filter((v) => {
      const regionOk = region === "全部" || v.region === region;
      const yearOk = year === "全部" || v.year >= yearThreshold(year);
      const typeOk = type === "全部" || v.type === type;
      return regionOk && yearOk && typeOk;
    });
    if (sort === "最新上传") {
      list = [...list].sort((a, b) => b.uploadedAt.localeCompare(a.uploadedAt));
    } else if (sort === "播放量") {
      list = [...list].sort((a, b) => b.views - a.views);
    } else {
      list = [...list].sort((a, b) => b.favorites - a.favorites);
    }
    return list;
  }, [baseList, region, year, type, sort]);

  usePageMeta(
    category
      ? `亲吻视频分类 - ${category.name} - 亲嘴视频`
      : "亲嘴视频 - 精选亲吻视频合集在线播放",
    category
      ? `亲吻视频分类「${category.name}」共收录 ${baseList.length} 部作品，${category.description}`
      : "亲嘴视频为您提供丰富的亲吻视频资源。"
  );

  if (!category) return <NotFound />;

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const resetPage = <T,>(setter: (v: T) => void) => (v: T) => {
    setter(v);
    setPage(1);
  };

  return (
    <SiteLayout>
      <div className="w-full px-4 md:px-8 py-8 md:py-10">
        <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-5 flex-wrap">
          <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">
            首页
          </Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-800">{category.name}</span>
        </nav>

        <header className="mb-7">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
              <i className={`${category.icon} text-xl`} />
            </div>
            <h1 className="font-heading text-2xl md:text-4xl text-foreground-950">
              亲吻视频分类 · {category.name}
            </h1>
          </div>
          <p className="text-sm text-foreground-600 leading-relaxed max-w-3xl">
            {category.description}
            <span className="ml-1 text-primary-600">
              当前共 {baseList.length} 部作品
            </span>
          </p>
        </header>

        <div className="mb-6">
          <button
            onClick={() => setFilterOpen((v) => !v)}
            className="lg:hidden inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-background-100 border border-background-200/70 text-sm text-foreground-700 cursor-pointer"
          >
            <i className="ri-filter-3-line" />
            筛选与排序
            <i className={filterOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
          </button>

          <div
            className={`${
              filterOpen ? "block" : "hidden"
            } lg:block mt-3 lg:mt-0 rounded-lg border border-background-200/70 bg-background-50 p-4 md:p-5 space-y-4`}
          >
            <ChipGroup
              label="地区"
              value={region}
              options={regionOptions}
              onChange={resetPage(setRegion)}
            />
            <ChipGroup
              label="年份"
              value={year}
              options={yearOptions}
              onChange={resetPage(setYear)}
            />
            <ChipGroup
              label="类型"
              value={type}
              options={typeOptions}
              onChange={resetPage(setType)}
            />
            <div className="h-px bg-background-200/70" />
            <ChipGroup
              label="排序"
              value={sort}
              options={sortOptions}
              onChange={resetPage(setSort)}
            />
          </div>
        </div>

        {paged.length > 0 ? (
          <div className="space-y-4 md:space-y-5">
            {paged.map((video, index) => (
              <VideoListItem key={video.id} video={video} index={index} />
            ))}
          </div>
        ) : (
          <div className="py-20 text-center rounded-lg bg-background-100 border border-background-200/70">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-400 mb-4">
              <i className="ri-emotion-sad-line text-3xl" />
            </div>
            <p className="text-foreground-700">当前筛选条件下没有找到视频</p>
            <button
              onClick={() => {
                setRegion("全部");
                setYear("全部");
                setType("全部");
                setPage(1);
              }}
              className="mt-5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              重置筛选
            </button>
          </div>
        )}

        {totalPages > 1 && (
          <div className="mt-10 flex items-center justify-center gap-2 flex-wrap">
            <button
              disabled={safePage === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="w-9 h-9 flex items-center justify-center rounded-md border border-background-300/70 text-foreground-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-primary-50 cursor-pointer transition-colors"
            >
              <i className="ri-arrow-left-s-line" />
            </button>
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                onClick={() => setPage(i + 1)}
                className={`w-9 h-9 flex items-center justify-center rounded-md text-sm transition-colors cursor-pointer ${
                  safePage === i + 1
                    ? "bg-primary-500 text-background-50"
                    : "border border-background-300/70 text-foreground-600 hover:bg-primary-50"
                }`}
              >
                {i + 1}
              </button>
            ))}
            <button
              disabled={safePage === totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="w-9 h-9 flex items-center justify-center rounded-md border border-background-300/70 text-foreground-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-primary-50 cursor-pointer transition-colors"
            >
              <i className="ri-arrow-right-s-line" />
            </button>
          </div>
        )}

        <div className="mt-12 rounded-lg bg-background-100 border border-background-200/70 p-5 md:p-6">
          <h2 className="font-heading text-lg text-foreground-950 mb-3">
            其他分类
          </h2>
          <div className="flex items-center gap-3 flex-wrap">
            {categories
              .filter((c) => c.key !== category.key)
              .map((c) => (
                <Link
                  key={c.key}
                  to={`/category/${c.key}`}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-50 border border-background-200/70 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className={c.icon} />
                  {c.name}
                </Link>
              ))}
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}